<template>
  <v-card class="d-flex flex-column task-history" flat tile>
    <div class="align-center body-1 d-flex font-weight-bold justify-space-between px-7 py-0 text-uppercase" style="height: 48px">
      <span>
        <v-icon class="mr-1" color="black" small>mdi-pulse</v-icon>
        Timeline
      </span>
      <div class="align-center d-flex">
        <v-icon
          aria-label="Ocultar histórico"
          color="grey darken-1"
          role="button"
          small
          tabindex="0"
          @click="$emit('alternar')"
          @keyup.enter="$emit('alternar')"
        >
          mdi-dock-right
        </v-icon>
      </div>
    </div>

    <v-divider />

    <v-card-text class="flex-grow-1 overflow-y-auto pa-0 task-history__content">
      <div v-if="!historico.length" class="align-center d-flex grey--text mt-3 px-6">
        <v-icon class="mr-2" color="grey" small>mdi-information-outline</v-icon>
        <span class="body-2">Nenhuma ação realizada até o momento.</span>
      </div>

      <v-expansion-panels v-else v-model="diaAberto" accordion class="px-4" flat>
        <v-expansion-panel v-for="(grupo, indice) in historicoPorDia" :key="grupo.data" class="timeline-day">
          <v-expansion-panel-header class="px-0 timeline-day__header" hide-actions>
            <v-icon class="mr-2" color="grey darken-1" small>
              {{ diaAberto === indice ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
            </v-icon>
            <span class="caption font-weight-bold grey--text text--darken-1 text-uppercase">{{ formatarDia(grupo.data) }}</span>
            <span class="caption grey--text ml-2">({{ grupo.registros.length }})</span>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="px-0">
            <v-timeline align-top class="mt-1" dense>
              <v-timeline-item v-for="registro in grupo.registros" :key="registro.id" color="primary" fill-dot small>
                <template v-slot:icon>
                  <v-avatar color="primary" size="24">
                    <span class="caption white--text">{{ iniciais(registro.usuario) }}</span>
                  </v-avatar>
                </template>

                <div class="caption font-weight-bold">
                  <v-tooltip v-if="registro.usuario && registro.usuario.length > 15" top>
                    <template v-slot:activator="{ attrs, on }">
                      <span v-bind="attrs" v-on="on">{{ registro.usuario.slice(0, 15) }}…</span>
                    </template>
                    <span>{{ registro.usuario }}</span>
                  </v-tooltip>
                  <span v-else>{{ registro.usuario || 'Usuário não informado' }}</span>
                  <span class="caption font-weight-regular grey--text ml-3">{{ formatarHorario(registro.data_execucao) }}</span>
                </div>
                <v-sheet class="mt-1 pa-2 timeline-event" outlined>
                  <span class="caption grey--text">Tarefa realizada:</span>
                  <s class="caption font-weight-medium">{{ ' ' + registro.titulo_tarefa }}</s>
                  <template v-if="registro.proxima_tarefa">
                    <span class="caption grey--text mx-1">→</span>
                    <span class="caption font-weight-medium">{{ registro.proxima_tarefa }}</span>
                  </template>
                  <div class="caption grey--text mt-1" style="white-space: pre-line">
                    <span class="caption grey--text">Observação:</span>
                    <v-tooltip v-if="observacaoLonga(registro.descricao)" top>
                      <template v-slot:activator="{ attrs, on }">
                        <span v-bind="attrs" class="caption font-weight-medium" v-on="on">
                          {{ ' ' + observacaoResumida(registro.descricao) }}
                        </span>
                      </template>
                      <span>{{ registro.descricao }}</span>
                    </v-tooltip>
                    <span v-else class="caption font-weight-medium">{{ ' ' + observacaoResumida(registro.descricao) }}</span>
                  </div>
                  <div class="mt-1">
                    <span class="caption grey--text">Status:</span>
                    <span class="caption font-weight-medium">{{ ' ' + rotuloStatusTarefa(registro.status_tarefa) }}</span>
                  </div>
                  <div class="mt-1">
                    <span class="caption grey--text">Resultado:</span>
                    <span class="caption font-weight-medium">{{ ' ' + rotuloResultadoTarefa(registro.resultado_tarefa) }}</span>
                  </div>
                </v-sheet>
              </v-timeline-item>
            </v-timeline>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>
  </v-card>
</template>

<script>
  const rotulosStatusTarefa = {
    PENDENTE: 'Pendente',
    EM_EXECUCAO: 'Em execução',
    CONCLUIDA: 'Concluída',
    CANCELADA: 'Cancelada',
    REJEITADA: 'Rejeitada'
  };

  const rotulosResultadoTarefa = {
    EXECUTADA_COM_SUCESSO: 'Executada com sucesso',
    EXECUTADA_PARCIALMENTE: 'Executada parcialmente',
    NAO_EXECUTADA: 'Não executada',
    BLOQUEADA: 'Bloqueada',
    JUSTIFICADA: 'Justificada'
  };

  export default {
    name: 'HistoricoTarefas',

    props: {
      historico: {
        type: Array,
        default: () => []
      }
    },

    data() {
      return {
        diaAberto: 0
      };
    },

    computed: {
      historicoPorDia() {
        const grupos = this.historico.reduce((resultado, registro) => {
          const data = String(registro.data_execucao || '').slice(0, 10) || 'sem-data';

          if (!resultado[data]) resultado[data] = [];

          resultado[data].push(registro);

          return resultado;
        }, {});

        return Object.keys(grupos)
          .sort((primeiraData, segundaData) => segundaData.localeCompare(primeiraData))
          .map((data) => ({
            data,
            registros: grupos[data].sort((primeiroRegistro, segundoRegistro) =>
              String(primeiroRegistro.data_execucao || '').localeCompare(String(segundoRegistro.data_execucao || ''))
            )
          }));
      }
    },

    methods: {
      formatarDia(data) {
        if (data === 'sem-data') return 'Data não informada';

        const [ano, mes, dia] = data.split('-');

        return ano && mes && dia ? `${dia}/${mes}/${ano}` : data;
      },

      formatarHorario(data) {
        return String(data || '').slice(11, 16) || '—';
      },

      iniciais(nome) {
        return String(nome || '?')
          .split(' ')
          .filter(Boolean)
          .slice(0, 2)
          .map((parte) => parte[0])
          .join('')
          .toUpperCase();
      },

      observacaoLonga(observacao) {
        return String(observacao || '').length > 50;
      },

      observacaoResumida(observacao) {
        const texto = String(observacao || '—');

        return this.observacaoLonga(texto) ? `${texto.slice(0, 100)}…` : texto;
      },

      rotuloStatusTarefa(status) {
        return rotulosStatusTarefa[status] || '—';
      },

      rotuloResultadoTarefa(resultado) {
        return rotulosResultadoTarefa[resultado] || '—';
      }
    }
  };
</script>

<style scoped>
  .task-history {
    height: 548px;
    max-height: calc(100vh - 172px);
    background-color: #d9d9d9 !important;
    color: #202020;
    overflow: hidden;
  }

  .timeline-day {
    background: transparent;
  }

  .timeline-event {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9;
    border-radius: 2px;
  }

  .timeline-event,
  .timeline-event .caption {
    font-size: 11px !important;
    line-height: 1.35;
  }

  .task-history .timeline-event .grey--text {
    color: #5a5a5a !important;
    font-size: 10px !important;
    font-weight: 500;
  }

  .timeline-day ::v-deep .v-expansion-panel-header {
    min-height: 40px;
  }

  .timeline-day ::v-deep .v-timeline-item {
    padding-bottom: 20px;
  }

  .timeline-day ::v-deep .v-timeline-item__divider {
    min-width: 52px;
  }

  .timeline-day ::v-deep .v-timeline--dense .v-timeline-item__body {
    max-width: calc(100% - 52px);
  }

  .timeline-day ::v-deep .v-timeline::before {
    display: none;
  }

  .task-history__content {
    flex: 1 1 0;
    min-height: 0;
    scrollbar-color: #9e9e9e #d5d5d5;
  }

  .task-history__content::-webkit-scrollbar {
    width: 8px;
  }

  .task-history__content::-webkit-scrollbar-track {
    background-color: #d5d5d5;
  }

  .task-history__content::-webkit-scrollbar-thumb {
    background-color: #9e9e9e;
    border-radius: 4px;
  }

  ::v-deep .v-expansion-panel-content__wrap {
    padding: 0;
    width: 100%;
  }

  ::v-deep .v-expansion-panel,
  ::v-deep .v-expansion-panel-content,
  ::v-deep .v-expansion-panel-content__wrap,
  ::v-deep .v-expansion-panels {
    background-color: #d9d9d9 !important;
  }

  .task-history ::v-deep .grey--text {
    color: #383838 !important;
  }

  .timeline-event .font-weight-medium {
    color: #202020;
    font-size: 11px !important;
    font-weight: 600;
  }

  ::v-deep .v-expansion-panel::before {
    box-shadow: none;
  }
</style>
